import React, { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { HorizontalActions } from './HorizontalActions'
import classNames from 'classnames'
import type { ClassValue } from 'classnames/types'
import type { Params } from '../packages/blueprint-components/src/useTableHooks'
import { Breadcrumbs } from './breadcrumbs/Breadcrumbs'
import type { BreadcrumbProps } from '@blueprintjs/core'
import { navStore } from './nav/Nav'
import { navV2Store } from './nav-v2/store'
import { isNavV2Enabled } from '../util/nav-version'

// ── BackButton ────────────────────────────────────────────────────────────────
// Unobtrusive fixed chevron in the top-left corner. Pass either `href` (SPA
// link) or `onBack` (callback) — whichever is appropriate for the context.

export const BackButton = observer(function BackButton({
    href,
    onBack,
}: {
    href?: string
    onBack?: () => void
}) {
    // Offset left edge by nav width when it's pinned open (not just hovering).
    // isHovered means the nav is an overlay — it doesn't push page content,
    // so we don't shift the button right in that case.
    const { search } = useLocation()
    const useNavV2 = isNavV2Enabled(search)
    const navPinned = navStore.open && navStore.isPinned && !navStore.isHovered
    const navWidth = useNavV2 ? navV2Store.widthStyle : '16em'
    const leftBase = navPinned ? `calc(${navWidth} + 20px)` : '20px'

    // Sit below any active top banner; bannerHeight is 0 when no banner shown.
    const topBase = navStore.bannerHeight + 12

    const style: React.CSSProperties = {
        left: leftBase,
        top: topBase,
    }

    const cls =
        'fixed z-20 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ' +
        'text-[14px] font-[500] text-[#8FA3B8] hover:text-[#0E2947] hover:bg-[#EEF1F5] ' +
        'transition-colors no-underline'

    const inner = (
        <>
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                <path
                    d="M10 2.5L5 7.5l5 5"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
            Back
        </>
    )

    if (href) {
        return (
            <Link to={href} className={cls} style={style}>
                {inner}
            </Link>
        )
    }
    return (
        <button type="button" onClick={onBack} className={cls} style={style}>
            {inner}
        </button>
    )
})

// ── Layout ────────────────────────────────────────────────────────────────────

type LayoutProps = {
    actions?: any
    primary?: any
    centerActions?: any
    rightActions?: any
    children?: any
    nested?: boolean
    className?: ClassValue
    layoutClassName?: ClassValue
    params?: Params
    source?: any
    breadcrumbs?: BreadcrumbProps[]
    /** Render an unobtrusive back button fixed to the top-left of the viewport. */
    backHref?: string
    /** Use instead of backHref when the back action is a callback (e.g. cancel). */
    onBack?: () => void
    // This should be an array of MenuItems
    extraMenuItems?: JSX.Element[]
}

export const Layout = ({
    actions,
    primary = null,
    centerActions,
    rightActions,
    children,
    nested = false,
    className,
    layoutClassName,
    params,
    source = null,
    breadcrumbs = [],
    backHref,
    onBack,
    extraMenuItems,
}: LayoutProps) => {
    const { pathname, search } = useLocation()
    const useNavV2 = useMemo(() => isNavV2Enabled(search), [search])
    const useNavV2FlatChrome =
        useNavV2 &&
        !nested &&
        (breadcrumbs?.[0]?.text === 'Manage' ||
            pathname === '/tools/ngrams' ||
            pathname === '/combined-history' ||
            pathname === '/promotions' ||
            pathname === '/smart-campaigns' ||
            pathname.startsWith('/admin'))

    const memoizedHorizontalActions = useMemo(() => {
        if (nested) {
            return
        }

        return (
            <HorizontalActions
                params={params}
                extraMenuItems={extraMenuItems}
                centerActions={centerActions}
                rightActions={rightActions}
            >
                <div className={classNames('px-1 min-w-0', useNavV2 && 'flex items-center gap-1')}>
                    <Breadcrumbs
                        breadcrumbs={breadcrumbs}
                        source={source ?? primary}
                        hideIcon={useNavV2}
                        fullWidth={!useNavV2}
                        style={useNavV2 ? { paddingLeft: 0 } : undefined}
                        className={
                            useNavV2 ? 'mj-breadcrumbs-nav-v2 min-w-0 flex-shrink' : undefined
                        }
                    />
                    {useNavV2 && (
                        <span className="text-[#9CA3AF] text-xs font-medium leading-none">/</span>
                    )}
                    <h3
                        style={{ paddingLeft: useNavV2 ? 0 : 18 }}
                        className={classNames(
                            useNavV2
                                ? 'm-0 pb-0 truncate text-[14px] font-medium text-[rgb(36,37,41)]'
                                : 'font-semibold text-base pb-1',
                        )}
                    >
                        {primary}
                    </h3>
                </div>
            </HorizontalActions>
        )
    }, [
        primary,
        params,
        source,
        breadcrumbs,
        centerActions,
        rightActions,
        extraMenuItems,
        nested,
        useNavV2,
    ])

    return (
        <div
            className={classNames(
                'h-full min-h-0',
                !nested && 'flex flex-col overflow-hidden',
                useNavV2FlatChrome && 'bg-white',
                className,
            )}
        >
            {(backHref || onBack) && <BackButton href={backHref} onBack={onBack} />}
            {memoizedHorizontalActions}
            {actions}
            <div
                className={classNames(
                    !nested && 'min-h-0 flex-1 overflow-auto overscroll-none',
                    nested && 'min-h-0',
                    { 'p-3 md:p-6': !nested && !useNavV2FlatChrome },
                    useNavV2FlatChrome && 'bg-white p-0 md:p-0',
                    layoutClassName,
                )}
            >
                {children}
            </div>
        </div>
    )
}
